import {
  type Location,
  MAX_LOCATION_NAME,
  NAME_PROBLEM_TEXT,
  normaliseLocationName,
  validateLocationName,
} from "@larder/shared";
import { useState } from "react";

/**
 * Renaming and removing households, from settings.
 *
 * Switching and ADDING places lives in the top bar and nowhere else (see the
 * note on `Settings`). What the top bar cannot do is change your mind about a
 * place that already exists, and "Home" — which every account is given without
 * being asked — is the one most people will want to rename. So: a list, a
 * pencil, a bin.
 *
 * Removing is two taps and both are on screen, rather than a native confirm.
 * The row itself turns into the question, says what it takes with it, and
 * offers "Keep" as the wide, easy target. Nothing about this is a dialog.
 *
 * The LAST place cannot be removed. The app would only make a fresh "Home"
 * the moment it was gone, so the tap would read as "everything vanished and
 * came back empty" — which is exactly what this product promises never to do.
 *
 * Only the owner gets the controls. The rules refuse a member anyway, and a
 * button that always fails is worse than no button.
 */
export function Households({
  places,
  currentId,
  uid,
  onRename,
  onRemove,
  countItems,
  busy,
}: {
  places: Location[];
  currentId: string | null;
  uid: string | null;
  onRename: (id: string, name: string) => void;
  onRemove: (id: string) => void;
  /** How many things are still in a place. Decides which confirm appears. */
  countItems: (id: string) => Promise<number>;
  busy: boolean;
}) {
  const [editing, setEditing] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  /** The place whose items are being counted, so the bin cannot be tapped twice. */
  const [counting, setCounting] = useState<string | null>(null);
  /** A place with things in it — the modal, not the row. */
  const [warning, setWarning] = useState<{ place: Location; items: number } | null>(null);

  /**
   * Two confirms, by what is at stake. An EMPTY place turns its own row into
   * the question — quiet, in place, easy to walk away from. A place with things
   * still in it gets a modal that names the number: that is a household's
   * larder, and a row-sized question was too easy to tap through.
   */
  async function askRemove(l: Location) {
    setEditing(null);
    setCounting(l.id);
    try {
      const items = await countItems(l.id);
      if (items > 0) setWarning({ place: l, items });
      else setRemoving(l.id);
    } catch {
      // Could not count: assume the worse case and ask the louder way.
      setWarning({ place: l, items: -1 });
    } finally {
      setCounting(null);
    }
  }

  const lastOne = places.length <= 1;

  return (
    <>
      <h3>
        Your places <span className="count">{places.length}</span>
      </h3>
      <p className="note">
        Each place keeps its own list and its own reminders. Add one from the name at the
        top of the screen.
      </p>

      <div className="place-list" data-testid="household-list">
        {places.map((l) => {
          const owner = uid !== null && l.ownerUid === uid;
          const current = l.id === currentId;
          const removable = owner && !lastOne;

          if (editing === l.id) {
            return (
              <RenameRow
                key={l.id}
                place={l}
                taken={places.filter((o) => o.id !== l.id).map((o) => o.name)}
                busy={busy}
                onSave={(name) => {
                  setEditing(null);
                  if (normaliseLocationName(name) !== l.name) onRename(l.id, name);
                }}
                onCancel={() => setEditing(null)}
              />
            );
          }

          if (removing === l.id) {
            return (
              <div
                key={l.id}
                className="place-item place-item-confirm"
                data-testid={`household-confirm-${l.name}`}
              >
                <span className="place-item-main">
                  <span className="item-name">Remove {l.name}?</span>
                  <span className="item-meta">
                    Everything in it goes too — the list, the history, the shopping list.
                    This can't be undone.
                  </span>
                  <span className="confirm-actions">
                    <button
                      type="button"
                      className="chip"
                      onClick={() => setRemoving(null)}
                      disabled={busy}
                      data-testid={`keep-household-${l.name}`}
                    >
                      Keep it
                    </button>
                    <button
                      type="button"
                      className="chip danger"
                      onClick={() => {
                        setRemoving(null);
                        onRemove(l.id);
                      }}
                      disabled={busy}
                      data-testid={`confirm-remove-household-${l.name}`}
                    >
                      Remove {l.name}
                    </button>
                  </span>
                </span>
              </div>
            );
          }

          return (
            <div
              key={l.id}
              className="place-item"
              data-testid={`household-item-${l.name}`}
            >
              <span className="place-item-main">
                <span className="item-name">{l.name}</span>
                <span className="item-meta">
                  {current ? "Open now" : owner ? "Yours" : "Shared with you"}
                </span>
              </span>
              {owner && (
                <button
                  type="button"
                  className="iconbtn"
                  onClick={() => {
                    setRemoving(null);
                    setEditing(l.id);
                  }}
                  disabled={busy}
                  aria-label={`Rename ${l.name}`}
                  data-testid={`rename-household-${l.name}`}
                >
                  <PencilIcon />
                </button>
              )}
              {owner && (
                <button
                  type="button"
                  className="iconbtn danger"
                  onClick={() => void askRemove(l)}
                  disabled={busy || !removable || counting !== null}
                  aria-label={`Remove ${l.name}`}
                  title={
                    lastOne ? "Add another place before removing this one" : undefined
                  }
                  data-testid={`remove-household-${l.name}`}
                >
                  <TrashIcon />
                </button>
              )}
            </div>
          );
        })}
      </div>
      {lastOne && (
        <p className="note" data-testid="household-last-note">
          Your only place can't be removed — add another first.
        </p>
      )}

      {warning && (
        <RemoveWarning
          place={warning.place}
          items={warning.items}
          busy={busy}
          onKeep={() => setWarning(null)}
          onRemove={() => {
            setWarning(null);
            onRemove(warning.place.id);
          }}
        />
      )}
    </>
  );
}

/**
 * The louder confirm, for a place that still has things in it.
 *
 * Same shell as the price intro: a scrim, a sheet, two answers. "Keep it" is
 * the primary and sits where the thumb lands; the destructive one is plain
 * text in red underneath, so the reflex tap is the safe one. The number is
 * the whole argument — "12 things" is what makes someone stop and check which
 * place this is.
 */
function RemoveWarning({
  place,
  items,
  busy,
  onKeep,
  onRemove,
}: {
  place: Location;
  /** -1 when the count itself failed. */
  items: number;
  busy: boolean;
  onKeep: () => void;
  onRemove: () => void;
}) {
  const what =
    items < 0
      ? "Everything in it"
      : items === 1
        ? "The 1 thing still in it"
        : `The ${items} things still in it`;
  return (
    <div className="modal-root" data-testid="remove-warning">
      <div className="scrim" />
      <div className="popup intro" role="dialog" aria-label={`remove ${place.name}`}>
        <h3 className="intro-title">Remove {place.name}?</h3>
        <p className="intro-line">
          <strong data-testid="remove-warning-count">{what}</strong> will be gone, along
          with the history and the shopping list.
        </p>
        <p className="intro-why">This can't be undone.</p>

        <button
          type="button"
          className="primary"
          disabled={busy}
          onClick={onKeep}
          data-testid="remove-warning-keep"
        >
          Keep {place.name}
        </button>
        <button
          type="button"
          className="ghost danger"
          disabled={busy}
          onClick={onRemove}
          data-testid="remove-warning-remove"
        >
          Remove it anyway
        </button>
      </div>
    </div>
  );
}

/**
 * The row, turned into a one-field form. Enter saves, Escape and the back
 * button cancel, and a name that has not changed is a cancel too.
 */
function RenameRow({
  place,
  taken,
  busy,
  onSave,
  onCancel,
}: {
  place: Location;
  /** The other places' names — clashing with yourself is not a clash. */
  taken: readonly string[];
  busy: boolean;
  onSave: (name: string) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(place.name);
  const [touched, setTouched] = useState(false);
  const problem = validateLocationName(name, taken);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (problem) return;
    onSave(name);
  }

  return (
    <form
      className="place-item place-item-edit"
      onSubmit={submit}
      data-testid={`household-edit-${place.name}`}
    >
      <span className="place-item-main">
        <input
          // biome-ignore lint/a11y/noAutofocus: the row the user just tapped "rename" on
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => setTouched(true)}
          onKeyDown={(e) => {
            if (e.key === "Escape") onCancel();
          }}
          maxLength={MAX_LOCATION_NAME + 10}
          autoComplete="off"
          enterKeyHint="done"
          aria-label={`New name for ${place.name}`}
          data-testid="household-name"
        />
        {touched && problem && (
          <span className="hint" data-testid="household-name-error">
            {NAME_PROBLEM_TEXT[problem]}
          </span>
        )}
      </span>
      <button
        type="button"
        className="iconbtn"
        onClick={onCancel}
        disabled={busy}
        aria-label="Cancel"
        data-testid="cancel-rename-household"
      >
        <CrossIcon />
      </button>
      <button
        type="submit"
        className="iconbtn ok"
        disabled={busy}
        aria-label={`Save name for ${place.name}`}
        data-testid="save-rename-household"
      >
        <CheckIcon />
      </button>
    </form>
  );
}

/* Heroicons, outline. Inline so they inherit `currentColor`. */

function PencilIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"
      />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
    </svg>
  );
}

function CrossIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
    </svg>
  );
}
